/**
 * The pictograms for what a plant is LIKE (FR-05, FR-06), inlined as SVG — its
 * water, sun, soil, size, care and strengths. The six that say what KIND of plant
 * it is are `CategoryIcons`; the ones that operate the app are
 * `icons/ControlIcons`.
 *
 * Drawn in the shared `icons/Icon` frame, which is also what `chrome/BrandIcons`
 * uses — inline so they inherit `currentColor`, which is what lets a water chip
 * tint its icon and its label from one token. The sets stay separate files
 * because they are edited for different reasons, not because they are drawn
 * differently.
 *
 * Most of these ship beside their own text label. The three on the browse card do
 * not: SPEC-012 D-2 draws the VALUE — a droplet count, a cloud against a sun —
 * and puts the words in a `.visually-hidden` span instead. Every glyph here is
 * still `aria-hidden`, and something adjacent still does the announcing; what
 * changed is whether that something has pixels.
 *
 * Drawn to the approved browse mockups (`documentation/starter/catalog-design`):
 * 1.5px strokes, round caps, botanical geometry. Shape names and props are the
 * contract; paths are not.
 */

import Icon from '../icons/Icon';

/**
 * A droplet, hollow or full.
 *
 * Filled is how much: the card counts three of these out and fills the plant's
 * score (SPEC-012 D-2). The inner curve is the highlight a hollow droplet needs
 * to read as a droplet at 14px, and it is dropped when the shape is filled
 * because a stroke inside a solid shape is a scratch.
 */
export function WaterIcon({ filled = false, ...rest }) {
    return (
        <Icon fill={filled ? 'currentColor' : 'none'} {...rest}>
            <path d="M12 3.4c3.3 3.7 5.1 6.5 5.1 8.9a5.1 5.1 0 0 1-10.2 0c0-2.4 1.8-5.2 5.1-8.9Z" />
            {filled ? null : <path d="M9.4 13.1c0 1.4.7 2.5 2 3.1" />}
        </Icon>
    );
}

/** Full sun. Filled, so the brightest band is also the heaviest glyph. */
export function SunIcon({ filled = false, ...rest }) {
    return (
        <Icon {...rest}>
            <circle cx="12" cy="12" r="3.6" fill={filled ? 'currentColor' : 'none'} />
            <path d="M12 3.2v2.1M12 18.7v2.1M3.2 12h2.1M18.7 12h2.1" />
            <path d="m5.8 5.8 1.5 1.5m9.4 9.4 1.5 1.5M5.8 18.2l1.5-1.5m9.4-9.4 1.5-1.5" />
        </Icon>
    );
}

/**
 * Sun or shade — a sun with a cloud across its lower half.
 *
 * The middle sun band needs to be tellable from the full one at 14px, and a sun
 * drawn with fewer rays is not: it just reads as the same glyph, badly. A second
 * shape is.
 */
export function SunCloudIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="13.4" cy="9.6" r="3.1" />
            <path d="M13.4 3.6v1.6M19.4 9.6h-1.6M17.7 5.3l-1.1 1.1M9 9.6H7.4M10.2 5.3l1.1 1.1" />
            <path d="M7.2 20.2a3 3 0 0 1-.4-6 4.3 4.3 0 0 1 8.2 1.1 2.5 2.5 0 0 1-.5 4.9Z" />
        </Icon>
    );
}

/** Shade — cloud only, which is the approved sheet's own glyph for it. */
export function CloudIcon(props) {
    return (
        <Icon {...props}>
            <path d="M7.4 19.4a3.6 3.6 0 0 1-.4-7.2 5 5 0 0 1 9.6 1.3 3 3 0 0 1-.6 5.9Z" />
        </Icon>
    );
}

/**
 * Crumbs over a mound, not a plant pot: the property is what the roots sit in,
 * and a pot reads as "container growing", which is a different fact entirely.
 * The crumbs are zero-length segments — a round cap paints them as dots.
 */
export function SoilIcon(props) {
    return (
        <Icon {...props}>
            <path d="M3.6 19.7c2.3-2.4 5.1-3.6 8.4-3.6s6.1 1.2 8.4 3.6Z" />
            <path d="M9.4 18.3h.01M13.9 18.6h.01M11.6 17.5h.01" />
            <path d="M8.2 13.7h.01M10.7 10.9h.01M14.1 10.4h.01M16 12.8h.01M12.5 13.5h.01" />
        </Icon>
    );
}

/**
 * Adaptable soil — the mound, solid.
 *
 * The three soil glyphs are one shape in three states, and each state is legible
 * at the 18px the card draws them at: ground that holds whatever you put in it is
 * FILLED, ground that drains carries the loose crumbs above it, and ground that
 * drains sharply is grit you can count. Three shapes that differed only in a dot
 * were three identical glyphs on a phone.
 */
export function SoilRichIcon(props) {
    return (
        <Icon {...props}>
            <path
                d="M2.6 20.2c2.6-3.4 5.7-5.1 9.4-5.1s6.8 1.7 9.4 5.1Z"
                fill="currentColor"
                fillOpacity="0.9"
            />
        </Icon>
    );
}

/** Sharply drained — grit over the mound, coarse enough that nothing sits wet. */
export function SoilGrittyIcon(props) {
    return (
        <Icon {...props}>
            <path d="M3.6 19.7c2.3-2.4 5.1-3.6 8.4-3.6s6.1 1.2 8.4 3.6Z" />
            <circle cx="8.8" cy="18.2" r="1.2" />
            <circle cx="12.2" cy="17.2" r="1.2" />
            <circle cx="15.6" cy="18.4" r="1.2" />
        </Icon>
    );
}

/** The size pictogram for anything measured in centimetres. */
export function SproutIcon(props) {
    return (
        <Icon {...props}>
            <path d="M12 20.8v-8.6" />
            <path d="M12 12.2c-1.4-1-2.1-2.4-2.1-4.2s.7-3.2 2.1-4.2c1.4 1 2.1 2.4 2.1 4.2s-.7 3.2-2.1 4.2Z" />
            <path d="M12 14.4c0-2.3 1.6-3.8 4.7-4.2-.3 2.9-1.9 4.2-4.7 4.2Z" />
            <path d="M12 16.7c0-2.3-1.6-3.8-4.7-4.2.3 2.9 1.9 4.2 4.7 4.2Z" />
        </Icon>
    );
}

/** The `trees` category, and the size pictogram for anything measured in metres. */
export function TreeIcon(props) {
    return (
        <Icon {...props}>
            <path d="M12 3.4c-1.5 0-2.7.8-3.2 2-1.6-.3-3.1.9-3.1 2.5 0 .5.1 1 .4 1.4-1.1.5-1.8 1.5-1.8 2.7 0 1.7 1.4 3 3.2 3h9c1.8 0 3.2-1.3 3.2-3 0-1.2-.7-2.2-1.8-2.7.3-.4.4-.9.4-1.4 0-1.6-1.5-2.8-3.1-2.5-.5-1.2-1.7-2-3.2-2Z" />
            <path d="M12 20.8V13" />
            <path d="m12 16.3-2.5-2.4M12 16.3l2.5-2.4" />
        </Icon>
    );
}

/**
 * Maintenance, and the only filled glyph in the set. The reference plate tints
 * this one leaf so "how much work is this?" is answerable without reading — the
 * other four properties are facts, and facts are drawn in outline.
 */
export function LeafIcon(props) {
    return (
        <Icon {...props}>
            <path
                d="M19.6 4.4C9.2 4.4 4.6 12 5.8 16c1.2.7 2.6 1.1 4.1 1.1 5.3 0 9.7-4.4 9.7-12.7Z"
                fill="currentColor"
                fillOpacity="0.2"
            />
            <path d="M17.2 6.7C12.7 8.3 8.4 12.3 5.2 19.4" />
        </Icon>
    );
}

export function SnowflakeIcon(props) {
    return (
        <Icon {...props}>
            <path d="M12 3v18M4.2 7.5l15.6 9M19.8 7.5l-15.6 9" />
            <path d="M10 5.2 12 3l2 2.2M14 18.8 12 21l-2-2.2" />
        </Icon>
    );
}

export function ButterflyIcon(props) {
    return (
        <Icon {...props}>
            <path d="M12 7.5v10" />
            <path d="M12 8.5C10.6 5.5 8.4 4 6.3 4.6 4.2 5.2 3.6 7.7 5 10c1.2 2 4 3 7 2.5" />
            <path d="M12 8.5c1.4-3 3.6-4.5 5.7-3.9C19.8 5.2 20.4 7.7 19 10c-1.2 2-4 3-7 2.5" />
            <path d="M12 12.5c-2.4 1.6-3.6 3.3-3 4.8.6 1.4 2 1.4 3 0 1 1.4 2.4 1.4 3 0 .6-1.5-.6-3.2-3-4.8Z" />
        </Icon>
    );
}

/** The sprig beside "Maintenance" on the detail plate — a whole small plant, not one leaf. */
export function SprigIcon(props) {
    return (
        <Icon {...props}>
            <path d="M12 21V6" />
            <path d="M12 10c0-2.2 1.5-3.7 3.9-4.1C15.9 8.1 14.4 10 12 10Z" />
            <path d="M12 10c0-2.2-1.5-3.7-3.9-4.1C8.1 8.1 9.6 10 12 10Z" />
            <path d="M12 16c0-2.2 1.5-3.7 3.9-4.1C15.9 14.1 14.4 16 12 16Z" />
            <path d="M12 16c0-2.2-1.5-3.7-3.9-4.1C8.1 14.1 9.6 16 12 16Z" />
        </Icon>
    );
}

export function WateringCanIcon(props) {
    return (
        <Icon {...props}>
            <path d="M4.5 11h9v6.4a2.6 2.6 0 0 1-2.6 2.6H7.1a2.6 2.6 0 0 1-2.6-2.6Z" />
            <path d="M13.5 12.6 19.4 8.7" />
            <path d="m17.5 6.8 3.4 3.4" />
            <path d="M6.8 11V9.7A2.7 2.7 0 0 1 12 8.8" />
        </Icon>
    );
}

export function ShearsIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="6.6" cy="17.6" r="2.3" />
            <circle cx="17.4" cy="17.6" r="2.3" />
            <path d="M8.3 16 17.4 4.2" />
            <path d="M15.7 16 6.6 4.2" />
        </Icon>
    );
}

export function SpadeIcon(props) {
    return (
        <Icon {...props}>
            <path d="M12 3.4v8.4" />
            <path d="M9.6 5.6h4.8" />
            <path d="M8.3 11.8h7.4l-1 5.3a3.8 3.8 0 0 1-5.4 0Z" />
        </Icon>
    );
}

/** The four decision properties keep a fixed icon, so the row reads the same on every plant. */
export const PROPERTY_ICONS = {
    water: WaterIcon,
    sun: SunIcon,
    soil: SoilIcon,
    size: SproutIcon,
};

/** Full sun is the filled sun, named so a lookup table can hold it. */
export function FullSunIcon(props) {
    return <SunIcon filled {...props} />;
}

/**
 * A glyph per BAND, so a property's value is drawn and not only named
 * (SPEC-012 D-2).
 *
 * Keyed by the band value `plantFilters` assigns, which is the same value the
 * sheet's tiles carry — the glyph beside "Full sun" in the sheet is the glyph on
 * the card of every plant that option would keep. Water is absent: its value is
 * drawn as a count of droplets rather than as a change of shape, which is the one
 * property the approved card meters.
 */
export const BAND_ICONS = {
    sun: {
        shade: CloudIcon,
        sunOrShade: SunCloudIcon,
        fullSun: FullSunIcon,
    },
    soil: {
        any: SoilRichIcon,
        wellDrained: SoilIcon,
        sharplyDrained: SoilGrittyIcon,
    },
    size: {
        small: SproutIcon,
        medium: SproutIcon,
        tall: TreeIcon,
        veryTall: TreeIcon,
    },
    maintenance: {
        easy: LeafIcon,
        some: ShearsIcon,
        regular: WateringCanIcon,
    },
};

/**
 * One glyph per care tip (SPEC-003 D-4). Deliberately the TOOL rather than the
 * property — a watering can beside "Water" is an instruction, where the droplet
 * beside it on the plate above is a fact about the plant.
 */
export const CARE_ICONS = {
    water: WateringCanIcon,
    pruning: ShearsIcon,
    planting: SpadeIcon,
    soil: SoilIcon,
};

export const TRAIT_ICONS = {
    'plants.trait.fullSun': SunIcon,
    'plants.trait.sunOrPartShade': SunIcon,
    'plants.trait.shadeTolerant': LeafIcon,
    'plants.trait.lowWater': WaterIcon,
    'plants.trait.veryLowWater': WaterIcon,
    'plants.trait.highWater': WaterIcon,
    'plants.trait.droughtTolerant': WaterIcon,
    'plants.trait.coldHardy': SnowflakeIcon,
    'plants.trait.tender': SproutIcon,
    'plants.trait.winterInterest': SnowflakeIcon,
    'plants.trait.pollinatorFriendly': ButterflyIcon,
    'plants.trait.easyCare': LeafIcon,
    'plants.trait.adaptableSoil': SoilIcon,
};
